import { useState, useEffect, useRef } from 'react';
import { Flex, Box, Input, Textarea, Switch } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuX, LuUpload, LuPlus } from 'react-icons/lu';
import { LANGUAGE_OPTIONS } from '../../constants/currencies';
import {
  createItem,
  updateItem,
  translateFields,
  type MenuItemResponse,
  type MenuItemOption,
  type TranslatableField,
  type BadgeDefinition,
} from '../../api/menus';
import { toast } from 'react-toastify';
import ModalShell, { CancelButton, SaveButton, DeleteIconButton, FieldLabel, LanguageTabs, inputStyles } from './ModalShell';
import ImagePickerModal from '../ImagePickerModal';

interface ItemModalProps {
  open: boolean;
  onClose: () => void;
  menuId: string;
  item: MenuItemResponse | null;
  isCreating: boolean;
  categoryId: string | null;
  availableLanguages: string[];
  defaultLanguage: string;
  currency: string;
  badgeDefinitions: BadgeDefinition[];
  onSaved: (item: MenuItemResponse, isNew: boolean, categoryId?: string) => void;
  onDelete: (itemId: string) => void;
  onImageReady: (blob: Blob) => Promise<void>;
  onRemoveImage: () => void;
}

const ItemModal = ({
  open,
  onClose,
  menuId,
  item,
  isCreating,
  categoryId,
  availableLanguages,
  defaultLanguage,
  currency,
  badgeDefinitions,
  onSaved,
  onDelete,
  onImageReady,
  onRemoveImage,
}: ItemModalProps) => {
  const { t } = useTranslation();
  const [names, setNames] = useState<TranslatableField>({});
  const [descriptions, setDescriptions] = useState<TranslatableField>({});
  const [price, setPrice] = useState('');
  const [badges, setBadges] = useState<string[]>([]);
  const [spicyLevel, setSpicyLevel] = useState<number | null>(null);
  const [options, setOptions] = useState<{ name: TranslatableField; price: string }[]>([]);
  const [visible, setVisible] = useState(true);
  const [soldOut, setSoldOut] = useState(false);
  const [editLang, setEditLang] = useState(defaultLanguage);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current) {
      if (isCreating) {
        setNames({});
        setDescriptions({});
        setPrice('');
        setBadges([]);
        setSpicyLevel(null);
        setOptions([]);
        setVisible(true);
        setSoldOut(false);
      } else if (item) {
        setNames({ ...item.name });
        setDescriptions(item.description ? { ...item.description } : {});
        setPrice(String(item.price));
        setBadges(item.badges || []);
        setSpicyLevel(item.spicy_level ?? null);
        setOptions(
          (item.options || []).map(o => ({ name: { ...o.name }, price: String(o.price) }))
        );
        setVisible(item.is_visible);
        setSoldOut(item.is_sold_out);
      }
      setEditLang(defaultLanguage);
    }
    prevOpen.current = open;
  }, [open, item, isCreating, defaultLanguage]);

  const handleImageReady = async (blob: Blob) => {
    await onImageReady(blob);
    setShowImagePicker(false);
  };

  const handleAutoTranslate = async () => {
    const defaultName = names[defaultLanguage]?.trim();
    const defaultDesc = descriptions[defaultLanguage]?.trim();
    // Collect option names from default language
    const optionDefaults = options.map((o, i) => ({ index: i, value: o.name[defaultLanguage]?.trim() || '' })).filter(o => o.value);

    if (!defaultName && !defaultDesc && optionDefaults.length === 0) {
      toast.error(t('menus.fillDefaultLanguageFirst', 'Please fill in the default language fields first.'));
      return;
    }

    const fields: { [key: string]: string } = {};
    if (defaultName) fields.name = defaultName;
    if (defaultDesc) fields.description = defaultDesc;
    optionDefaults.forEach(o => { fields[`option_${o.index}`] = o.value; });

    const targetLanguages = availableLanguages.filter(lang => {
      if (lang === defaultLanguage) return false;
      const nameEmpty = !names[lang]?.trim();
      const descEmpty = !descriptions[lang]?.trim();
      const anyOptionEmpty = optionDefaults.some(o => !options[o.index].name[lang]?.trim());
      return nameEmpty || descEmpty || anyOptionEmpty;
    });

    if (targetLanguages.length === 0) {
      toast.info(t('menus.allFieldsFilled', 'All translation fields are already filled.'));
      return;
    }

    setIsTranslating(true);
    try {
      const result = await translateFields(menuId, { fields, target_languages: targetLanguages });
      setNames(prev => {
        const updated = { ...prev };
        for (const lang of targetLanguages) {
          if (!updated[lang]?.trim() && result.translations[lang]?.name) {
            updated[lang] = result.translations[lang].name;
          }
        }
        return updated;
      });
      setDescriptions(prev => {
        const updated = { ...prev };
        for (const lang of targetLanguages) {
          if (!updated[lang]?.trim() && result.translations[lang]?.description) {
            updated[lang] = result.translations[lang].description;
          }
        }
        return updated;
      });
      setOptions(prev => {
        const updated = prev.map((o) => ({ ...o, name: { ...o.name } }));
        for (const lang of targetLanguages) {
          const langTranslations = result.translations[lang];
          if (!langTranslations) continue;
          optionDefaults.forEach(od => {
            const key = `option_${od.index}`;
            if (!updated[od.index].name[lang]?.trim() && langTranslations[key]) {
              updated[od.index].name[lang] = langTranslations[key];
            }
          });
        }
        return updated;
      });
      toast.success(t('menus.translationComplete', 'Translations filled. Review and save.'));
    } catch (error) {
      toast.error(t('menus.translationFailed', 'Auto-translation failed.'));
      console.error(error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const parsed = parseFloat(price);
      const cleanNames = Object.fromEntries(Object.entries(names).filter(([, v]) => v.trim()));
      const cleanDescs = Object.fromEntries(Object.entries(descriptions).filter(([, v]) => v.trim()));
      const descPayload = Object.keys(cleanDescs).length > 0 ? cleanDescs : undefined;

      const optionsPayload: MenuItemOption[] | null = options.length > 0
        ? options
            .filter(o => Object.values(o.name).some(v => v.trim()))
            .map(o => ({
              name: Object.fromEntries(Object.entries(o.name).filter(([, v]) => v.trim())),
              price: parseFloat(o.price) || 0,
            }))
        : null;

      if (isCreating && categoryId && menuId) {
        const newItem = await createItem(menuId, categoryId, {
          name: cleanNames,
          description: descPayload,
          price: !isNaN(parsed) ? parsed : 0,
          badges,
          spicy_level: spicyLevel,
          options: optionsPayload,
          is_visible: visible,
          is_sold_out: soldOut,
        });
        onSaved(newItem, true, categoryId);
      } else if (item) {
        const updated = await updateItem(item.id, {
          name: cleanNames,
          description: descPayload,
          price: !isNaN(parsed) ? parsed : item.price,
          badges,
          spicy_level: spicyLevel,
          options: optionsPayload,
          is_visible: visible,
          is_sold_out: soldOut,
        });
        onSaved(updated, false);
      }
      onClose();
    } catch (error) {
      toast.error(isCreating ? t('menus.createItemFailed') : t('menus.updateItemFailed'));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={isCreating ? t('menus.addItem') : t('menus.editItem')}
      maxW="480px"
      footer={
        <>
          {item && !isCreating && (
            <DeleteIconButton onClick={() => onDelete(item.id)} />
          )}
          <CancelButton onClick={onClose} label={t('common.cancel')} />
          <SaveButton onClick={handleSave} loading={isSaving} label={t('common.save')} />
        </>
      }
    >
      <Flex direction="column" rowGap="16px">
        <LanguageTabs
          languages={availableLanguages}
          activeLang={editLang}
          onSelect={setEditLang}
          options={LANGUAGE_OPTIONS}
          onAutoTranslate={availableLanguages.length > 1 ? handleAutoTranslate : undefined}
          isTranslating={isTranslating}
        />

        {/* Basic info section */}
        <Box p="16px" borderRadius="8px" backgroundColor="neutral.700">
          <Flex direction="column" rowGap="16px">
            {/* Image */}
            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.image')}</FieldLabel>
              {item?.image_url ? (
                <Box position="relative" width="80px" height="80px">
                  <Box
                    width="80px"
                    height="80px"
                    borderRadius="8px"
                    borderWidth="1px"
                    borderColor="neutral.600"
                    overflow="hidden"
                    cursor="pointer"
                    onClick={() => setShowImagePicker(true)}
                    _hover={{ borderColor: 'neutral.500' }}
                  >
                    <img src={item.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <button
                    type="button"
                    onClick={onRemoveImage}
                    style={{
                      position: 'absolute', top: -6, right: -6,
                      width: 20, height: 20, padding: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: '#111111', border: '1px solid #383838',
                      borderRadius: '50%', cursor: 'pointer', zIndex: 2,
                    }}
                  >
                    <LuX size={10} color="#9CA4B1" />
                  </button>
                </Box>
              ) : (
                <Flex
                  alignItems="center"
                  gap="12px"
                  py="12px"
                  px="16px"
                  borderRadius="8px"
                  borderWidth="1px"
                  borderStyle="dashed"
                  borderColor="neutral.600"
                  backgroundColor="neutral.800"
                  cursor="pointer"
                  transition="all 0.2s"
                  onClick={() => setShowImagePicker(true)}
                  _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.600' }}
                >
                  <Box p="8px" borderRadius="8px" backgroundColor="neutral.600" flexShrink={0}>
                    <LuUpload size={18} color="#CFD5DE" />
                  </Box>
                  <Flex direction="column" gap="2px">
                    <Box fontSize="14px" fontWeight="400" color="neutral.300">{t('menus.uploadImage')}</Box>
                    <Box fontSize="12px" fontWeight="400" color="neutral.500">PNG, JPG, WebP</Box>
                  </Flex>
                </Flex>
              )}
              <ImagePickerModal
                open={showImagePicker}
                onClose={() => setShowImagePicker(false)}
                onImageReady={handleImageReady}
              />
            </Flex>

            {/* Name */}
            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.itemName')}</FieldLabel>
              <Input
                value={names[editLang] || ''}
                onChange={(e) => setNames(prev => ({ ...prev, [editLang]: e.target.value }))}
                placeholder={t('menus.itemName')}
                {...inputStyles}
                backgroundColor="neutral.800"
              />
            </Flex>

            {/* Description */}
            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.itemDescription')}</FieldLabel>
              <Textarea
                value={descriptions[editLang] || ''}
                onChange={(e) => setDescriptions(prev => ({ ...prev, [editLang]: e.target.value }))}
                placeholder={t('menus.itemDescription')}
                {...inputStyles}
                backgroundColor="neutral.800"
                rows={3}
              />
            </Flex>

            {/* Price */}
            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.price')}</FieldLabel>
              <Flex alignItems="center" gap="8px">
                <Input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  {...inputStyles}
                  backgroundColor="neutral.800"
                  width="160px"
                />
                <Box fontSize="14px" fontWeight="400" color="neutral.500">{currency}</Box>
              </Flex>
            </Flex>
          </Flex>
        </Box>

        {/* Options / Add-ons section */}
        <Box p="16px" borderRadius="8px" backgroundColor="neutral.700">
          <Flex direction="column" rowGap="8px">
            <FieldLabel>{t('menus.options', 'Options / Add-ons')}</FieldLabel>
            <Flex direction="column" gap="8px">
              {options.map((opt, idx) => (
                <Flex key={idx} gap="8px" alignItems="center">
                  <Input
                    value={opt.name[editLang] || ''}
                    onChange={(e) => {
                      const updated = [...options];
                      updated[idx] = { ...updated[idx], name: { ...updated[idx].name, [editLang]: e.target.value } };
                      setOptions(updated);
                    }}
                    placeholder={t('menus.optionName', 'Option name')}
                    {...inputStyles}
                    backgroundColor="neutral.800"
                    flex="1"
                  />
                  <Input
                    value={opt.price}
                    onChange={(e) => {
                      const updated = [...options];
                      updated[idx] = { ...updated[idx], price: e.target.value };
                      setOptions(updated);
                    }}
                    placeholder="0.00"
                    {...inputStyles}
                    backgroundColor="neutral.800"
                    width="90px"
                  />
                  <Box
                    cursor="pointer"
                    color="neutral.500"
                    _hover={{ color: 'neutral.300' }}
                    flexShrink={0}
                    onClick={() => setOptions(prev => prev.filter((_, i) => i !== idx))}
                  >
                    <LuX size={16} />
                  </Box>
                </Flex>
              ))}
              <Flex
                alignItems="center"
                gap="6px"
                cursor="pointer"
                color="neutral.400"
                _hover={{ color: 'neutral.300' }}
                onClick={() => setOptions(prev => [...prev, { name: {}, price: '' }])}
              >
                <LuPlus size={14} />
                <Box fontSize="13px" fontWeight="400">{t('menus.addOption', 'Add option')}</Box>
              </Flex>
            </Flex>
          </Flex>
        </Box>

        {/* Badges & Spicy section */}
        <Box p="16px" borderRadius="8px" backgroundColor="neutral.700">
          <Flex direction="column" rowGap="16px">
            {/* Badges */}
            {badgeDefinitions.length > 0 && (
              <Flex direction="column" rowGap="8px">
                <FieldLabel>{t('menus.badges')}</FieldLabel>
                <Flex gap="8px" flexWrap="wrap">
                  {badgeDefinitions.map((badge) => {
                    const isSelected = badges.includes(badge.key);
                    return (
                      <Box
                        key={badge.key}
                        px="10px"
                        py="5px"
                        fontSize="13px"
                        fontWeight="400"
                        color={isSelected ? badge.color : 'neutral.500'}
                        backgroundColor={isSelected ? `${badge.color}18` : 'transparent'}
                        borderWidth="1px"
                        borderColor={isSelected ? badge.color : 'neutral.600'}
                        borderRadius="6px"
                        cursor="pointer"
                        transition="all 0.15s"
                        _hover={{ borderColor: badge.color }}
                        onClick={() => {
                          setBadges(prev =>
                            isSelected ? prev.filter(b => b !== badge.key) : [...prev, badge.key]
                          );
                        }}
                      >
                        {badge.label[defaultLanguage] || Object.values(badge.label)[0] || badge.key}
                      </Box>
                    );
                  })}
                </Flex>
              </Flex>
            )}

            {/* Spicy Level */}
            <Flex direction="column" rowGap="8px">
              <FieldLabel>Spicy Level</FieldLabel>
              <Flex gap="8px" alignItems="center">
                {[1, 2, 3, 4].map((level) => (
                  <ToggleChip
                    key={level}
                    active={spicyLevel === level}
                    onClick={() => setSpicyLevel(prev => prev === level ? null : level)}
                  >
                    {Array.from({ length: level }).map(() => '🌶️').join('')}
                  </ToggleChip>
                ))}
              </Flex>
            </Flex>
          </Flex>
        </Box>

        {/* Toggles */}
        <Box p="16px" borderRadius="8px" backgroundColor="neutral.700">
          <Flex gap="24px">
            <Flex alignItems="center" gap="8px">
              <Switch.Root checked={visible} onCheckedChange={(e) => setVisible(e.checked)}>
                <Switch.HiddenInput />
                <Switch.Control><Switch.Thumb /></Switch.Control>
              </Switch.Root>
              <Box fontSize="14px" color="neutral.300">{t('menus.visible')}</Box>
            </Flex>
            <Flex alignItems="center" gap="8px">
              <Switch.Root checked={soldOut} onCheckedChange={(e) => setSoldOut(e.checked)}>
                <Switch.HiddenInput />
                <Switch.Control><Switch.Thumb /></Switch.Control>
              </Switch.Root>
              <Box fontSize="14px" color="neutral.300">{t('menus.soldOut')}</Box>
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </ModalShell>
  );
};

const ToggleChip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <Box
    px="10px"
    py="5px"
    fontSize="13px"
    fontWeight="400"
    color={active ? 'neutral.900' : 'neutral.500'}
    backgroundColor={active ? 'neutral.100' : 'transparent'}
    borderWidth="1px"
    borderColor={active ? 'neutral.100' : 'neutral.600'}
    borderRadius="6px"
    cursor="pointer"
    _hover={{ borderColor: 'neutral.500' }}
    onClick={onClick}
  >
    {children}
  </Box>
);

export default ItemModal;
